import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService, addressService } from '../services/api';
import { updateMetaTags } from '../utils/seo';

function Profile() {
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.roles?.some((role) => ['admin', 'super_admin'].includes(role.slug));
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    city: user?.city || '',
    state: user?.state || '',
  });
  const [message, setMessage] = useState({ type: 'info', text: '' });
  const [saving, setSaving] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addressMessage, setAddressMessage] = useState({ type: 'info', text: '' });
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressForm, setAddressForm] = useState({
    type: 'shipping',
    full_name: user?.name || '',
    phone: user?.phone || '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    is_default: true,
  });

  useEffect(() => {
    setAddressForm((prev) => ({
      ...prev,
      full_name: user?.name || '',
      phone: user?.phone || '',
    }));
  }, [user?.name, user?.phone]);

  useEffect(() => {
    updateMetaTags({
      title: 'My Profile',
      description: 'Manage your Ortus FMCG account profile and addresses.',
      siteName: 'Ortus FMCG'
    });
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    try {
      const list = await addressService.index();
      setAddresses(Array.isArray(list) ? list : list?.data || []);
    } catch (error) {
      console.error('Address load error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await authService.updateProfile({
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
      });
      await refreshUser();
      setMessage({ type: 'success', text: 'Profile updated successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Unable to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setAddressMessage({ type: 'info', text: '' });
    try {
      setAddressSaving(true);
      await addressService.store({
        type: addressForm.type,
        full_name: addressForm.full_name,
        phone: addressForm.phone,
        address_line_1: addressForm.address_line1,
        address_line_2: addressForm.address_line2,
        city: addressForm.city,
        state: addressForm.state,
        postal_code: addressForm.postal_code,
        country: addressForm.country,
        is_default: addressForm.is_default,
      });
      setAddressMessage({ type: 'success', text: 'Address saved.' });
      setAddressForm((prev) => ({
        ...prev,
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'India',
      }));
      await loadAddresses();
    } catch (error) {
      setAddressMessage({ type: 'error', text: error.response?.data?.message || 'Unable to save address.' });
    } finally {
      setAddressSaving(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Remove this address?')) return;
    try {
      await addressService.destroy(id);
      await loadAddresses();
    } catch (error) {
      setAddressMessage({ type: 'error', text: error.response?.data?.message || 'Unable to delete address.' });
    }
  };

  const shippingAddresses = addresses.filter((address) => address.type === 'shipping');
  const billingAddresses = addresses.filter((address) => address.type === 'billing');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-red-600 mb-2">Account</p>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900">My Profile</h1>
            <p className="text-slate-500 mt-3 max-w-2xl">Update your personal information, manage saved addresses, and keep your ecommerce preferences up to date.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/shop" className="px-5 py-3 rounded-2xl border border-slate-200 text-slate-700 font-semibold hover:border-red-500">Return to Shop</Link>
            {isAdmin && (
              <Link to="/admin" className="px-5 py-3 rounded-2xl border border-slate-200 text-slate-700 font-semibold hover:border-red-500">Admin</Link>
            )}
            <Link to="/customer-dashboard" className="px-5 py-3 rounded-2xl bg-slate-900 text-white font-semibold hover:bg-red-700">My Dashboard</Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {message.text && (
              <div className={`mb-4 px-4 py-3 rounded-2xl text-sm font-semibold flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-red-50 text-red-600'
                  : message.type === 'error'
                  ? 'bg-rose-50 text-rose-600'
                  : 'bg-slate-50 text-slate-500'
              }`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Full Name</label>
                  <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3 focus:border-red-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Email</label>
                  <input value={formData.email} disabled className="w-full border border-slate-100 rounded-2xl px-4 py-3 bg-slate-50 text-slate-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Phone</label>
                  <input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3 focus:border-red-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">City</label>
                  <input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3 focus:border-red-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">State</label>
                  <input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3 focus:border-red-500 outline-none" />
                </div>
              </div>
              <button type="submit" disabled={saving} className="px-10 py-4 bg-red-600 text-white rounded-2xl font-black text-sm tracking-wide uppercase shadow-lg shadow-red-200 hover:bg-red-700 disabled:opacity-60">
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </form>

            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-black text-slate-900">Shipping & Billing Addresses</h2>
                  <p className="text-xs text-slate-400 mt-1">{shippingAddresses.length} shipping • {billingAddresses.length} billing</p>
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">{addresses.length} saved</span>
              </div>

              {addressMessage.text && (
                <div className={`px-4 py-3 rounded-2xl text-sm font-semibold ${
                  addressMessage.type === 'success'
                    ? 'bg-red-50 text-red-600'
                    : addressMessage.type === 'error'
                    ? 'bg-rose-50 text-rose-600'
                    : 'bg-slate-50 text-slate-500'
                }`}
                >
                  {addressMessage.text}
                </div>
              )}

              <div className="space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-black uppercase tracking-[0.3em] text-slate-500">Shipping Addresses</h3>
                    <span className="text-xs text-slate-400">{shippingAddresses.length} on file</span>
                  </div>
                  {shippingAddresses.length > 0 ? (
                    <div className="space-y-4">
                      {shippingAddresses.map((address) => (
                        <div key={address.id} className="border border-slate-100 rounded-2xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <p className="text-sm font-black text-slate-900 flex items-center gap-3">
                              {address.full_name}
                              <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">{address.type}</span>
                              {address.is_default && (
                                <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] font-bold uppercase tracking-widest">Default</span>
                              )}
                            </p>
                            <p className="text-sm text-slate-500">{address.phone}</p>
                            <p className="text-sm text-slate-500 mt-2">{address.address_line1}</p>
                            {address.address_line2 && <p className="text-sm text-slate-500">{address.address_line2}</p>}
                            <p className="text-sm text-slate-500">{address.city}, {address.state} {address.postal_code}</p>
                            <p className="text-xs text-slate-400 uppercase tracking-[0.3em] mt-1">{address.country}</p>
                          </div>
                          <button onClick={() => handleDeleteAddress(address.id)} className="text-rose-600 text-sm font-semibold">Remove</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">You haven’t saved any shipping addresses yet.</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-black uppercase tracking-[0.3em] text-slate-500">Billing Addresses</h3>
                    <span className="text-xs text-slate-400">{billingAddresses.length} on file</span>
                  </div>
                  {billingAddresses.length > 0 ? (
                    <div className="space-y-4">
                      {billingAddresses.map((address) => (
                        <div key={address.id} className="border border-slate-100 rounded-2xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <p className="text-sm font-black text-slate-900 flex items-center gap-3">
                              {address.full_name}
                              <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">{address.type}</span>
                              {address.is_default && (
                                <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] font-bold uppercase tracking-widest">Default</span>
                              )}
                            </p>
                            <p className="text-sm text-slate-500">{address.phone}</p>
                            <p className="text-sm text-slate-500 mt-2">{address.address_line1}</p>
                            {address.address_line2 && <p className="text-sm text-slate-500">{address.address_line2}</p>}
                            <p className="text-sm text-slate-500">{address.city}, {address.state} {address.postal_code}</p>
                            <p className="text-xs text-slate-400 uppercase tracking-[0.3em] mt-1">{address.country}</p>
                          </div>
                          <button onClick={() => handleDeleteAddress(address.id)} className="text-rose-600 text-sm font-semibold">Remove</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">You haven’t saved any billing addresses yet.</p>
                  )}
                </div>
              </div>

              <form onSubmit={handleAddressSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Type</label>
                  <select value={addressForm.type} onChange={(e) => setAddressForm({ ...addressForm, type: e.target.value })} className="w-full border border-slate-200 rounded-2xl px-4 py-3">
                    <option value="shipping">Shipping</option>
                    <option value="billing">Billing</option>
                  </select>
                </div>
                <label className="flex items-center text-xs font-bold uppercase tracking-widest text-slate-400 gap-2">
                  <input type="checkbox" checked={addressForm.is_default} onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })} className="w-4 h-4" />
                  Make default
                </label>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Full Name</label>
                  <input value={addressForm.full_name} onChange={(e) => setAddressForm({ ...addressForm, full_name: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Phone</label>
                  <input value={addressForm.phone} onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Address Line 1</label>
                  <input value={addressForm.address_line1} onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Address Line 2</label>
                  <input value={addressForm.address_line2} onChange={(e) => setAddressForm({ ...addressForm, address_line2: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">City</label>
                  <input value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">State</label>
                  <input value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Postal Code</label>
                  <input value={addressForm.postal_code} onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block">Country</label>
                  <input value={addressForm.country} onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })} className="w-full border border-slate-100 rounded-2xl px-4 py-3" />
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={addressSaving} className="w-full md:w-auto px-8 py-3 bg-slate-900 text-white rounded-2xl font-semibold hover:bg-red-700 disabled:opacity-60">
                    {addressSaving ? 'Saving...' : 'Save Address'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-[0.25em] mb-4">Quick Links</h3>
              <div className="space-y-3">
                <Link to="/shop" className="flex items-center justify-between px-4 py-3 rounded-2xl bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-700">
                  <span className="font-semibold">Shop Dashboard</span>
                  <span className="text-xs font-bold uppercase tracking-widest">View</span>
                </Link>
                <Link to="/profile" className="flex items-center justify-between px-4 py-3 rounded-2xl bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-700">
                  <span className="font-semibold">Manage Addresses</span>
                  <span className="text-xs font-bold uppercase tracking-widest">Open</span>
                </Link>
                <Link to="/checkout" className="flex items-center justify-between px-4 py-3 rounded-2xl bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-700">
                  <span className="font-semibold">Checkout</span>
                  <span className="text-xs font-bold uppercase tracking-widest">Continue</span>
                </Link>
              </div>
            </div>

            <div className="bg-slate-900 rounded-3xl text-white p-6 shadow-lg">
              <p className="text-xs font-bold uppercase tracking-[0.4em] text-slate-400">Support</p>
              <h3 className="text-2xl font-black mt-3">Need help with orders or payments?</h3>
              <p className="text-slate-300 text-sm mt-3">Chat with an Ortus ecommerce specialist for order tracking, payment issues, or returns.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 mt-6 text-sm font-bold uppercase tracking-widest">
                Contact Support →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
